import React, { useEffect, useState } from 'react';
import { getWebSocketUrl } from '../services/api';
import { AlertLog } from '../types';

interface AlertPayload {
  alert_id: string;
  name: string;
  device_id: string;
  metric: string;
  operator: string;
  threshold: string | number;
  actual_value: string | number;
  triggered_at: string;
}

function formatToastValue(value: string | number | undefined | null): string {
  if (value === undefined || value === null) return '—';
  if (typeof value === 'number') return value.toFixed(1);
  const num = parseFloat(value);
  if (!isNaN(num) && value.trim() !== '') return num.toFixed(1);
  return value;
}

export default function AlertToast() {
  const [toasts, setToasts] = useState<(AlertPayload & { id: number })[]>([]);

  useEffect(() => {
    const wsUrl = getWebSocketUrl('/ws');
    const ws = new WebSocket(wsUrl);

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);
        if (msg.type === 'alert') {
          const payload: AlertPayload = msg.data;
          const toastId = Date.now();
          setToasts((prev) => [...prev.slice(-2), { ...payload, id: toastId }]);
          setTimeout(() => {
            setToasts((prev) => prev.filter((t) => t.id !== toastId));
          }, 6000);
        }
      } catch {
        // ignore
      }
    };

    return () => ws.close();
  }, []);

  if (toasts.length === 0) return null;

  return (
    <div style={styles.container}>
      {toasts.map((toast) => (
        <div key={toast.id} style={styles.toast} className="animate-slide-in">
          <div style={styles.icon}>⚠</div>
          <div style={styles.content}>
            <div style={styles.title}>{toast.name}</div>
            <div style={styles.body}>
              {toast.metric} {toast.operator} {toast.threshold} · actual {formatToastValue(toast.actual_value)}
            </div>
          </div>
          <button
            style={styles.close}
            onClick={() => setToasts((prev) => prev.filter((t) => t.id !== toast.id))}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    position: 'fixed',
    top: '1.5rem',
    right: '1.5rem',
    zIndex: 100,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
    maxWidth: '380px',
  },
  toast: {
    background: 'rgba(22, 25, 34, 0.95)',
    backdropFilter: 'blur(16px)',
    border: '1px solid rgba(232, 96, 44, 0.3)',
    borderRadius: '12px',
    padding: '1rem',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '0.75rem',
    boxShadow: '0 8px 32px rgba(232, 96, 44, 0.15), 0 0 0 1px rgba(232, 96, 44, 0.1)',
    color: '#f8fafc',
  },
  icon: {
    fontSize: '1.25rem',
    lineHeight: 1,
  },
  content: {
    flex: 1,
  },
  title: {
    fontWeight: 700,
    fontSize: '0.9rem',
    marginBottom: '2px',
  },
  body: {
    fontSize: '0.8rem',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
  },
  close: {
    background: 'none',
    border: 'none',
    color: '#9ca3af',
    fontSize: '1.25rem',
    cursor: 'pointer',
    lineHeight: 1,
    padding: 0,
  },
};
